import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FiSearch, FiShield, FiUsers, FiEdit, FiUserX, FiUserCheck, FiDownload, FiX, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import app from '../../config/firebase';
import { useAuth } from '../../AuthContext';
import { exportUsersCsv, formatDate, toDate, userName } from './adminUtils';

const FILTERS = ['All', 'Business owners', 'Admins', 'Deactivated'];

const updateUser = (payload) => httpsCallable(getFunctions(app), 'adminUpdateUser')(payload);

export default function AdminUsers() {
  const { users, setUsers, businesses, setBusinesses } = useOutletContext();
  const { currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [editing, setEditing] = useState(null); // form state for the edit modal
  const [confirmStatus, setConfirmStatus] = useState(null); // { user, archive }
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const businessesByOwner = businesses.reduce((acc, b) => {
    (acc[b.ownerId] ||= []).push(b);
    return acc;
  }, {});

  const q = query.trim().toLowerCase();
  const visible = users
    .filter(u => {
      if (filter === 'Business owners') return businessesByOwner[u.id];
      if (filter === 'Admins') return u.isAdmin;
      if (filter === 'Deactivated') return u.isSuspended;
      return true;
    })
    .filter(u => !q || `${userName(u)} ${u.email} ${u.phone} ${u.id}`.toLowerCase().includes(q))
    .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));

  const closeModals = () => {
    if (saving) return;
    setEditing(null);
    setConfirmStatus(null);
    setModalError('');
  };

  const openEdit = (u) => {
    setModalError('');
    setEditing({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, phone: u.phone || '', isAdmin: u.isAdmin });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setModalError('');
    const { id, ...changes } = editing;
    try {
      await updateUser({ userId: id, changes });
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, ...changes, email: changes.email.trim().toLowerCase() } : u)));
      setEditing(null);
      setToast(`${userName(changes)} updated.`);
    } catch (err) {
      console.error('Error updating user:', err);
      setModalError(err.message || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async () => {
    const { user, archive } = confirmStatus;
    const isSuspended = !user.isSuspended;
    setSaving(true);
    setModalError('');
    try {
      const result = await updateUser({ userId: user.id, changes: { isSuspended }, archiveBusinesses: isSuspended && archive });
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, isSuspended } : u)));
      if (result.data.archivedBusinesses > 0) {
        setBusinesses(prev => prev.map(b => (b.ownerId === user.id ? { ...b, isArchived: true } : b)));
      }
      setConfirmStatus(null);
      setToast(`${userName(user)} ${isSuspended ? 'deactivated' : 'reactivated'}.`);
    } catch (err) {
      console.error('Error changing user status:', err);
      setModalError(err.message || "Couldn't change this user's status.");
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: editing[key],
    onChange: (e) => setEditing(prev => ({ ...prev, [key]: e.target.value })),
  });

  return (
    <div className="admin-section">
      <div className="admin-toolbar">
        <div className="admin-filters">
          {FILTERS.map(f => (
            <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        <label className="admin-search">
          <FiSearch aria-hidden="true" />
          <input type="search" placeholder="Search name, email, phone or ID" value={query} onChange={e => setQuery(e.target.value)} />
        </label>
        <button type="button" className="admin-logout-button" onClick={() => exportUsersCsv(visible, businesses)} disabled={visible.length === 0}>
          <FiDownload aria-hidden="true" /> <span>Export CSV</span>
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="empty-state"><FiUsers size={40} /><p>No users in this view.</p></div>
      ) : (
        <div className="admin-panel admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Phone</th><th>Businesses</th><th>Payouts</th><th>Joined</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {visible.map(u => (
                <tr key={u.id} className={u.isSuspended ? 'admin-row-muted' : ''}>
                  <td>
                    {userName(u)}
                    {u.isAdmin && <span className="badge-verified admin-inline-badge"><FiShield /> Admin</span>}
                    {u.isSuspended && <span className="badge-archived admin-inline-badge">Deactivated</span>}
                  </td>
                  <td>{u.email ? <a href={`mailto:${u.email}`}>{u.email}</a> : '—'}</td>
                  <td>{u.phone || '—'}</td>
                  {(() => {
                    const names = (businessesByOwner[u.id] || []).map(b => b.businessName).join(', ');
                    return <td className="admin-cell-truncate" title={names}>{names || '—'}</td>;
                  })()}
                  <td>{u.hasPaystack ? 'Paystack ready' : u.hasBanking ? 'Bank details only' : '—'}</td>
                  <td>{formatDate(u.createdAt)}</td>
                  <td>
                    <div className="admin-row-actions">
                      <button type="button" className="icon-text-btn" onClick={() => openEdit(u)}><FiEdit /> Edit</button>
                      {u.id !== currentUser?.uid && (u.isSuspended ? (
                        <button type="button" className="icon-text-btn primary" onClick={() => setConfirmStatus({ user: u, archive: false })}>
                          <FiUserCheck /> Reactivate
                        </button>
                      ) : (
                        <button type="button" className="icon-text-btn danger" onClick={() => setConfirmStatus({ user: u, archive: true })}>
                          <FiUserX /> Deactivate
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="admin-modal-overlay" onClick={closeModals}>
          <form className="admin-modal admin-form-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveEdit}>
            <button type="button" className="admin-modal-close" onClick={closeModals} aria-label="Close"><FiX /></button>
            <h2>Edit user</h2>
            {modalError && <div className="form-error"><FiAlertTriangle /> {modalError}</div>}
            <div className="admin-form-grid">
              <label>First name<input {...field('firstName')} /></label>
              <label>Last name<input {...field('lastName')} /></label>
              <label>Email<input type="email" required {...field('email')} /></label>
              <label>Phone<input type="tel" {...field('phone')} /></label>
            </div>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={editing.isAdmin}
                disabled={editing.id === currentUser?.uid}
                onChange={(e) => setEditing(prev => ({ ...prev, isAdmin: e.target.checked }))}
              />
              Admin access (can open this console and receives admin emails)
            </label>
            <p className="admin-muted">Changing the email also changes the address they sign in with.</p>
            <div className="admin-modal-actions">
              <button type="button" className="btn-secondary" onClick={closeModals} disabled={saving}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                <FiCheckCircle /> {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmStatus && (
        <div className="admin-modal-overlay" onClick={closeModals}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="admin-modal-close" onClick={closeModals} aria-label="Close"><FiX /></button>
            <div className="admin-modal-icon">{confirmStatus.user.isSuspended ? <FiUserCheck /> : <FiUserX />}</div>
            <h2>{confirmStatus.user.isSuspended ? 'Reactivate' : 'Deactivate'} {userName(confirmStatus.user)}?</h2>
            {modalError && <div className="form-error"><FiAlertTriangle /> {modalError}</div>}
            {confirmStatus.user.isSuspended ? (
              <p>They'll be able to sign in again. Archived businesses stay archived until you unarchive them.</p>
            ) : (
              <>
                <p>They'll be signed out and won't be able to sign in on the website or the app. Their data is kept and you can reactivate them any time.</p>
                {businessesByOwner[confirmStatus.user.id] && (
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={confirmStatus.archive}
                      onChange={(e) => setConfirmStatus(prev => ({ ...prev, archive: e.target.checked }))}
                    />
                    Also archive their {businessesByOwner[confirmStatus.user.id].length === 1 ? 'business' : 'businesses'} ({businessesByOwner[confirmStatus.user.id].map(b => b.businessName).join(', ')})
                  </label>
                )}
              </>
            )}
            <div className="admin-modal-actions">
              <button type="button" className="btn-secondary" onClick={closeModals} disabled={saving}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveStatus} disabled={saving}>
                {saving ? 'Saving…' : confirmStatus.user.isSuspended ? 'Yes, reactivate' : 'Yes, deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="admin-toast"><FiCheckCircle /><span>{toast}</span></div>
      )}
    </div>
  );
}
